import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import {
	ClockIcon,
	ChartBarIcon,
	WrenchScrewdriverIcon,
	ArrowPathIcon,
	TicketIcon,
	PencilIcon,
	TrashIcon,
	CheckIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';

import { StackableModal, StatusBadge, DateTimePicker } from '../components/nextgen';
import { DangerModal } from '../components/Shell/Modal';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import {
	getTicketStatistics,
	getWorkOrderStatistics,
	updateActivity,
	deleteActivity,
} from '../api';
import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { getUserFullName } from '../components/helpers/functions';

const MODAL_NAME = 'statisticsModal';

const StatCard = ({ icon: Icon, label, value, subtext, className = '' }) => (
	<div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
		<div className="flex items-center gap-2 mb-2">
			{Icon && <Icon className="h-4 w-4 text-gray-500" />}
			<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
				{label}
			</span>
		</div>
		<div className="text-lg font-semibold text-gray-900">{value || '-'}</div>
		{subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
	</div>
);

const TimelineItem = ({
	title,
	timestamp,
	isLast = false,
	badge = null,
	icon: CustomIcon = null,
	activityId = null,
	canEdit = false,
	onEdit = null,
	onDelete = null,
	isEditing = false,
	editingDate = null,
	onEditDateChange = null,
	onSaveEdit = null,
	onCancelEdit = null,
}) => {
	return (
		<div className="relative flex gap-4 group">
			{!isLast && (
				<div className="absolute left-[7px] top-8 h-full w-px bg-gray-200" />
			)}
			<div className="flex-shrink-0">
				{CustomIcon ? (
					<div className="h-4 w-4 rounded-full bg-amber-500 border-2 border-white shadow flex items-center justify-center">
						<CustomIcon className="h-2.5 w-2.5 text-white" />
					</div>
				) : (
					<div className="h-4 w-4 rounded-full bg-indigo-500 border-2 border-white shadow" />
				)}
			</div>
			<div className="flex-1 pb-4">
				<div className="flex items-center justify-between">
					<span className="text-sm font-medium text-gray-900">{title}</span>
					<div className="flex items-center gap-2">
						{badge}
						{canEdit && !isEditing && (
							<div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
								<button
									onClick={() => onEdit?.(activityId, timestamp)}
									className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
									title="Edit date"
								>
									<PencilIcon className="h-3.5 w-3.5" />
								</button>
								<button
									onClick={() => onDelete?.(activityId)}
									className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
									title="Delete"
								>
									<TrashIcon className="h-3.5 w-3.5" />
								</button>
							</div>
						)}
					</div>
				</div>
				{isEditing ? (
					<div className="flex items-center gap-2 mt-1">
						<DateTimePicker
							value={editingDate}
							onChange={onEditDateChange}
							className="text-xs"
						/>
						<button
							onClick={onSaveEdit}
							className="p-1 text-green-600 hover:bg-green-50 rounded"
							title="Save"
						>
							<CheckIcon className="h-4 w-4" />
						</button>
						<button
							onClick={onCancelEdit}
							className="p-1 text-gray-400 hover:bg-gray-100 rounded"
							title="Cancel"
						>
							<XMarkIcon className="h-4 w-4" />
						</button>
					</div>
				) : (
					timestamp && (
						<span className="text-xs text-gray-500">
							{moment(timestamp).format('MMM D, YYYY [at] h:mmA')}
						</span>
					)
				)}
			</div>
		</div>
	);
};

const WorkOrderTimelineCard = ({ workOrder }) => {
	const { timeline, durations } = workOrder;

	return (
		<div className="border border-gray-200 rounded-lg p-4 bg-white">
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<WrenchScrewdriverIcon className="h-4 w-4 text-gray-500" />
					<span className="font-medium text-sm text-gray-900">
						{workOrder.workOrderCode}
					</span>
				</div>
				<StatusBadge status={workOrder.status} />
			</div>
			<div className="text-sm text-gray-700 mb-3">{workOrder.title}</div>

			<div className="space-y-2 text-xs">
				{timeline.createdAt && (
					<div className="flex justify-between">
						<span className="text-gray-500">Created</span>
						<span className="text-gray-700">
							{moment(timeline.createdAt).format('MMM D, YYYY [at] h:mmA')}
						</span>
					</div>
				)}
				{timeline.scheduledStartDate && (
					<div className="flex justify-between">
						<span className="text-gray-500">Scheduled</span>
						<span className="text-gray-700">
							{moment(timeline.scheduledStartDate).format(
								'MMM D, YYYY [at] h:mmA',
							)}
						</span>
					</div>
				)}
				{timeline.completedDate && (
					<div className="flex justify-between">
						<span className="text-gray-500">Completed</span>
						<span className="text-gray-700">
							{moment(timeline.completedDate).format('MMM D, YYYY [at] h:mmA')}
						</span>
					</div>
				)}
			</div>

			{(durations.creationToScheduled || durations.scheduledToCompletion) && (
				<div className="mt-3 pt-3 border-t border-gray-100">
					<div className="flex gap-4 text-xs">
						{durations.creationToScheduled && (
							<div>
								<span className="text-gray-500">Time to schedule: </span>
								<span className="font-medium text-gray-700">
									{durations.creationToScheduled.formatted}
								</span>
							</div>
						)}
						{durations.scheduledToCompletion && (
							<div>
								<span className="text-gray-500">Time to complete: </span>
								<span className="font-medium text-gray-700">
									{durations.scheduledToCompletion.formatted}
								</span>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const formatTicketStatus = (status) => {
	const statusMap = {
		incoming: 'Open',
		inprogress: 'In Progress',
		done: 'Resolved',
		closed: 'Closed',
	};
	return statusMap[status] || status;
};

const formatWorkOrderStatus = (status) => {
	const statusMap = {
		draft: 'Draft',
		pending: 'Pending',
		scheduled: 'Scheduled',
		in_progress: 'In Progress',
		on_hold: 'On Hold',
		completed: 'Completed',
		cancelled: 'Cancelled',
	};
	return statusMap[status] || status;
};

const StatisticsModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	const entityType = open?.entityType || 'ticket';
	const entityId = open?.id;

	// State for editing activities
	const [editingActivityId, setEditingActivityId] = useState(null);
	const [editingDate, setEditingDate] = useState(null);
	const [isUpdating, setIsUpdating] = useState(false);
	const [deleteConfirmActivityId, setDeleteConfirmActivityId] = useState(null);

	const {
		value: ticketStats,
		status: ticketStatus,
		execute: fetchTicketStats,
	} = useAsync(getTicketStatistics, { immediate: false });

	const {
		value: workOrderStats,
		status: workOrderStatus,
		execute: fetchWorkOrderStats,
	} = useAsync(getWorkOrderStatistics, { immediate: false });

	const refreshStatistics = () => {
		if (entityId) {
			if (entityType === 'ticket') {
				fetchTicketStats({ id: entityId });
			} else if (entityType === 'workorder') {
				fetchWorkOrderStats({ id: entityId });
			}
		}
	};

	useEffect(() => {
		refreshStatistics();
	}, [entityId, entityType]);

	// Reset editing state when modal closes
	useEffect(() => {
		if (!open) {
			setEditingActivityId(null);
			setEditingDate(null);
		}
	}, [open]);

	const handleEdit = (activityId, currentTimestamp) => {
		setEditingActivityId(activityId);
		setEditingDate(new Date(currentTimestamp));
	};

	const handleCancelEdit = () => {
		setEditingActivityId(null);
		setEditingDate(null);
	};

	const handleSaveEdit = async () => {
		if (!editingActivityId || !editingDate) return;

		setIsUpdating(true);
		try {
			await updateActivity({
				id: editingActivityId,
				createdAt: editingDate.toISOString(),
			});
			setEditingActivityId(null);
			setEditingDate(null);
			refreshStatistics();
		} catch (error) {
			console.error('Failed to update activity:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	const handleDelete = async (activityId) => {
		setIsUpdating(true);
		try {
			await deleteActivity({ id: activityId });
			refreshStatistics();
		} catch (error) {
			console.error('Failed to delete activity:', error);
		} finally {
			setIsUpdating(false);
			setDeleteConfirmActivityId(null);
		}
	};

	const isLoading =
		entityType === 'ticket'
			? ticketStatus === 'pending'
			: workOrderStatus === 'pending';

	const statistics = entityType === 'ticket' ? ticketStats : workOrderStats;
	const formatStatus =
		entityType === 'ticket' ? formatTicketStatus : formatWorkOrderStatus;

	// Build timeline events
	const timelineEvents = useMemo(() => {
		if (!statistics) return [];

		const events = [];

		// Add status transitions
		statistics.statusTransitions?.forEach((transition) => {
			events.push({
				type: 'status',
				activityId: transition.activityId || null,
				timestamp: transition.timestamp,
				title: transition.from
					? `${formatStatus(transition.from)} → ${formatStatus(transition.to)}`
					: entityType === 'ticket'
						? `Ticket created as ${formatStatus(transition.to)}`
						: `Work Order created as ${formatStatus(transition.to)}`,
				badge: transition.addedBy
					? `by ${getUserFullName(transition.addedBy)}`
					: null,
				canEdit: !!transition.activityId,
			});
		});

		// For tickets, add work order creation events
		if (entityType === 'ticket' && statistics.workOrders) {
			statistics.workOrders.forEach((wo) => {
				if (wo.timeline?.createdAt) {
					events.push({
						type: 'workorder',
						activityId: null,
						timestamp: wo.timeline.createdAt,
						title: `Work Order created: ${wo.workOrderCode}`,
						subtitle: wo.title,
						canEdit: false,
					});
				}
			});
		}

		// For work orders, add ticket reference if exists
		if (entityType === 'workorder' && statistics.ticket) {
			// We could add ticket-related events here if needed
		}

		// Sort by timestamp
		events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

		return events;
	}, [statistics, entityType, formatStatus]);

	const renderTicketContent = () => (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="text-lg font-semibold text-gray-900">
						{statistics.ticketCode}
					</span>
					<StatusBadge status={statistics.currentStatus} />
				</div>
			</div>

			{/* Key Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<StatCard
					icon={ClockIcon}
					label="Time to First Response"
					value={statistics.durations.timeToFirstResponse?.formatted}
					subtext={
						statistics.durations.timeToFirstResponse
							? 'From ticket creation to in-progress'
							: 'Not yet in progress'
					}
				/>
				<StatCard
					icon={ChartBarIcon}
					label="Time to Resolution"
					value={statistics.durations.timeToResolution?.formatted}
					subtext={
						statistics.durations.timeToResolution
							? `From in-progress to ${formatStatus(statistics.currentStatus)}`
							: 'Not yet resolved'
					}
				/>
				<StatCard
					icon={ClockIcon}
					label="Total Lifecycle"
					value={statistics.durations.totalLifecycleTime?.formatted}
					subtext={
						statistics.durations.totalLifecycleTime
							? 'From creation to resolution'
							: 'Still open'
					}
				/>
			</div>

			{/* Reopened Info */}
			{statistics.reopenedInfo?.wasReopened && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
					<div className="flex items-center gap-2">
						<ArrowPathIcon className="h-4 w-4 text-amber-600" />
						<span className="text-sm font-medium text-amber-800">
							This ticket was reopened {statistics.reopenedInfo.reopenCount}{' '}
							time
							{statistics.reopenedInfo.reopenCount > 1 ? 's' : ''}
						</span>
					</div>
					<p className="text-xs text-amber-700 mt-1">
						Statistics show time to the most recent resolution.
					</p>
				</div>
			)}

			{/* Timeline */}
			{renderTimeline()}

			{/* Work Orders */}
			{statistics.workOrders?.length > 0 && (
				<div>
					<h3 className="text-sm font-medium text-gray-900 mb-4">
						Work Orders ({statistics.workOrders.length})
					</h3>
					<div className="space-y-3">
						{statistics.workOrders.map((wo) => (
							<WorkOrderTimelineCard key={wo.id} workOrder={wo} />
						))}
					</div>
				</div>
			)}

			{/* Timestamps */}
			<div className="border-t border-gray-200 pt-4">
				<div className="grid grid-cols-2 gap-4 text-xs">
					<div>
						<span className="text-gray-500">Created: </span>
						<span className="text-gray-700">
							{moment(statistics.createdAt).format('MMM D, YYYY [at] h:mmA')}
						</span>
					</div>
					{statistics.reopenedInfo?.lastResolvedAt && (
						<div>
							<span className="text-gray-500">Last Resolved: </span>
							<span className="text-gray-700">
								{moment(statistics.reopenedInfo.lastResolvedAt).format(
									'MMM D, YYYY [at] h:mmA',
								)}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);

	const renderWorkOrderContent = () => (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<div className="flex items-center gap-2">
						<span className="text-lg font-semibold text-gray-900">
							{statistics.workOrderCode}
						</span>
						<StatusBadge status={statistics.currentStatus} />
					</div>
					{statistics.title && (
						<p className="text-sm text-gray-600 mt-1">{statistics.title}</p>
					)}
				</div>
			</div>

			{/* Linked Ticket */}
			{statistics.ticket && (
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
					<div className="flex items-center gap-2">
						<TicketIcon className="h-4 w-4 text-blue-600" />
						<span className="text-sm font-medium text-blue-800">
							Linked to Ticket: {statistics.ticket.ticketCode}
						</span>
					</div>
					<p className="text-xs text-blue-700 mt-1">
						{statistics.ticket.subject}
					</p>
				</div>
			)}

			{/* Key Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<StatCard
					icon={ClockIcon}
					label="Time to Schedule"
					value={statistics.durations.creationToScheduled?.formatted}
					subtext={
						statistics.durations.creationToScheduled
							? 'From creation to scheduled'
							: 'Not yet scheduled'
					}
				/>
				<StatCard
					icon={ChartBarIcon}
					label="Time to Start"
					value={statistics.durations.creationToStart?.formatted}
					subtext={
						statistics.durations.creationToStart
							? 'From creation to in-progress'
							: 'Not yet started'
					}
				/>
				<StatCard
					icon={ClockIcon}
					label="Work Duration"
					value={statistics.durations.startToCompletion?.formatted}
					subtext={
						statistics.durations.startToCompletion
							? 'From start to completion'
							: 'Not yet completed'
					}
				/>
			</div>

			{/* Total Lifecycle */}
			{statistics.durations.totalLifecycleTime && (
				<div className="bg-gray-50 rounded-lg p-4">
					<div className="flex items-center justify-between">
						<div>
							<span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
								Total Lifecycle Time
							</span>
							<div className="text-2xl font-semibold text-gray-900 mt-1">
								{statistics.durations.totalLifecycleTime.formatted}
							</div>
						</div>
						<div className="text-xs text-gray-500 text-right">
							<div>
								Created:{' '}
								{moment(statistics.createdAt).format('MMM D, YYYY h:mmA')}
							</div>
							<div>
								Completed:{' '}
								{moment(statistics.completedDate).format('MMM D, YYYY h:mmA')}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Reopened Info */}
			{statistics.reopenedInfo?.wasReopened && (
				<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
					<div className="flex items-center gap-2">
						<ArrowPathIcon className="h-4 w-4 text-amber-600" />
						<span className="text-sm font-medium text-amber-800">
							This work order was reopened {statistics.reopenedInfo.reopenCount}{' '}
							time
							{statistics.reopenedInfo.reopenCount > 1 ? 's' : ''}
						</span>
					</div>
				</div>
			)}

			{/* Timeline */}
			{renderTimeline()}

			{/* Key Dates */}
			<div className="border-t border-gray-200 pt-4">
				<div className="grid grid-cols-2 gap-4 text-xs">
					<div>
						<span className="text-gray-500">Created: </span>
						<span className="text-gray-700">
							{moment(statistics.createdAt).format('MMM D, YYYY [at] h:mmA')}
						</span>
					</div>
					{statistics.scheduledStartDate && (
						<div>
							<span className="text-gray-500">Scheduled: </span>
							<span className="text-gray-700">
								{moment(statistics.scheduledStartDate).format(
									'MMM D, YYYY [at] h:mmA',
								)}
							</span>
						</div>
					)}
					{statistics.completedDate && (
						<div>
							<span className="text-gray-500">Completed: </span>
							<span className="text-gray-700">
								{moment(statistics.completedDate).format(
									'MMM D, YYYY [at] h:mmA',
								)}
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);

	const renderTimeline = () => (
		<div>
			<h3 className="text-sm font-medium text-gray-900 mb-4">Timeline</h3>
			<div className="bg-gray-50 rounded-lg p-4">
				{timelineEvents.map((event, idx) => (
					<TimelineItem
						key={`${event.type}-${event.activityId || idx}`}
						title={event.title}
						timestamp={event.timestamp}
						isLast={idx === timelineEvents.length - 1}
						icon={event.type === 'workorder' ? WrenchScrewdriverIcon : null}
						badge={
							event.badge ? (
								<span className="text-xs text-gray-500">{event.badge}</span>
							) : event.subtitle ? (
								<span className="text-xs text-gray-500">{event.subtitle}</span>
							) : null
						}
						activityId={event.activityId}
						canEdit={event.canEdit}
						onEdit={handleEdit}
						onDelete={(activityId) => setDeleteConfirmActivityId(activityId)}
						isEditing={editingActivityId === event.activityId}
						editingDate={editingActivityId === event.activityId ? editingDate : null}
						onEditDateChange={setEditingDate}
						onSaveEdit={handleSaveEdit}
						onCancelEdit={handleCancelEdit}
					/>
				))}
			</div>
		</div>
	);

	const modalTitle =
		entityType === 'ticket' ? 'Ticket Statistics' : 'Work Order Statistics';

	return (
		<>
			<StackableModal
				open={!!open}
				onClose={() => setClose()}
				size="4xl"
				title={modalTitle}
			>
				{isLoading ? (
					<LoadingSkeleton />
				) : statistics ? (
					entityType === 'ticket' ? (
						renderTicketContent()
					) : (
						renderWorkOrderContent()
					)
				) : (
					<div className="text-center py-8 text-gray-500">
						No statistics available
					</div>
				)}
			</StackableModal>

			<DangerModal
				open={!!deleteConfirmActivityId}
				onClose={() => setDeleteConfirmActivityId(null)}
				title="Delete Activity"
				confirmText="Delete"
				onConfirm={() => handleDelete(deleteConfirmActivityId)}
			>
				Are you sure you want to delete this activity? This action cannot be undone.
			</DangerModal>
		</>
	);
};

export default StatisticsModal;
